import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Chip,
  Typography,
  Paper
} from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon, FilterList as FilterIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';

// Enhanced styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
  border: `1px solid ${theme.palette.divider}`,
  background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    '&:hover': {
      backgroundColor: theme.palette.grey[50],
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.background.paper,
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
    },
  },
}));

const StyledButton = styled(Button)(() => ({
  borderRadius: 8,
  textTransform: 'none',
  fontWeight: 600,
  padding: '8px 16px',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  '&:hover': {
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
    transform: 'translateY(-1px)',
  },
}));

const DataFilters = ({ 
  filters, 
  onFilterChange, 
  onClearFilters, 
  onApplyFilters,
  loading = false,
  sourceOptions = [],
  integrationOptions = []
}) => {
  const handleFilterChange = (field, value) => {
    onFilterChange(field, value);
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(value => value && value !== '').length;
  };

  return (
    <StyledPaper sx={{ mb: 3, p: 3 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <FilterIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Filters
          </Typography>
          {getActiveFiltersCount() > 0 && (
            <Chip 
              label={`${getActiveFiltersCount()} active`} 
              size="small" 
              color="primary" 
              sx={{ 
                fontWeight: 600,
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.7 },
                  '100%': { opacity: 1 },
                }
              }}
            />
          )}
        </Box>
      </Box>
      
      <Grid container spacing={2} alignItems="stretch">
        {/* Search */}
        <Grid item xs={12} sm={6} md={3}>
          <StyledTextField
            fullWidth
            size="small"
            label="Search"
            placeholder="Name, email, phone..."
            value={filters.search || ''}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
            }}
          />
        </Grid>

        {/* Data Source Filter */}
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Source</InputLabel>
            <Select
              value={filters.data_source || ''}
              onChange={(e) => handleFilterChange('data_source', e.target.value)}
              label="Source"
            >
              <MenuItem value="">All Sources</MenuItem>
              {sourceOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Integration Source Filter */}
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Integration</InputLabel>
            <Select
              value={filters.integration_source || ''}
              onChange={(e) => handleFilterChange('integration_source', e.target.value)}
              label="Integration"
            >
              <MenuItem value="">All Integrations</MenuItem>
              {integrationOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Country Filter */}
        <Grid item xs={12} sm={6} md={2}>
          <StyledTextField
            fullWidth
            size="small"
            label="Country"
            placeholder="UAE, USA, UK..."
            value={filters.country || ''}
            onChange={(e) => handleFilterChange('country', e.target.value)}
          />
        </Grid>

        {/* Funnel Filter */}
        <Grid item xs={12} sm={6} md={2}>
          <StyledTextField
            fullWidth
            size="small"
            label="Funnel"
            placeholder="Product name..."
            value={filters.funnel || ''}
            onChange={(e) => handleFilterChange('funnel', e.target.value)}
          />
        </Grid>

        {/* Action Buttons */}
        <Grid item xs={12} sm={12} md={12}>
          <Box display="flex" gap={1} justifyContent="flex-end" flexWrap="wrap">
            <StyledButton
              variant="contained"
              size="medium"
              onClick={onApplyFilters}
              disabled={loading}
              startIcon={<SearchIcon />}
              sx={{ minWidth: 120 }}
            >
              {loading ? 'Filtering...' : 'Apply Filters'}
            </StyledButton>
            <StyledButton
              variant="outlined"
              size="medium"
              onClick={onClearFilters}
              startIcon={<ClearIcon />}
              sx={{ minWidth: 120 }}
            >
              Clear All
            </StyledButton>
          </Box>
        </Grid>
      </Grid>
    </StyledPaper>
  );
};

DataFilters.propTypes = {
  filters: PropTypes.shape({
    search: PropTypes.string,
    data_source: PropTypes.string,
    integration_source: PropTypes.string,
    country: PropTypes.string,
    funnel: PropTypes.string,
  }),
  onFilterChange: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  onApplyFilters: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  sourceOptions: PropTypes.array,
  integrationOptions: PropTypes.array,
};

DataFilters.defaultProps = {
  filters: {
    search: '',
    data_source: '',
    integration_source: '',
    country: '',
    funnel: '',
  },
  loading: false,
  sourceOptions: [],
  integrationOptions: [],
};

export default DataFilters;
